import type { AxiosError } from "axios";
import { worktimeApi } from "@wkt/api/worktime";
import type { GenericServiceResponseDTO } from "@wkt/api/profileDTO";
import type {
  DocumentsInfoDTO,
  SignRequestValidationDTO,
} from "./documentsDTO";

type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;
type RequestOptions = {
  force?: boolean;
};

const DOCUMENTS_INFO_CACHE_TTL_MS = 5000;

type DocumentUploadRequest = {
  fileName: string;
  contentType: string;
  documentFile: string;
  itemId: number;
};

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const [, base64Content = result] = result.split(",", 2);
      resolve(base64Content);
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error("Error reading file"));
    };

    reader.readAsDataURL(file);
  });
}

export class DocumentsAPI {
  private documentsInfoCache: DocumentsInfoDTO | null = null;
  private documentsInfoCachedAt = 0;
  private documentsInfoRequest: Promise<DocumentsInfoDTO> | null = null;

  getDocumentsInfo(
    onSuccess: SuccessCallback<DocumentsInfoDTO>,
    onError: ErrorCallback,
    options: RequestOptions = {},
  ): void {
    this.fetchDocumentsInfo(options)
      .then(onSuccess)
      .catch((error: AxiosError) => onError(error));
  }

  invalidateDocumentsInfo(): void {
    this.documentsInfoCache = null;
    this.documentsInfoCachedAt = 0;
  }

  getDocumentById(
    documentId: number,
    onSuccess: SuccessCallback<unknown>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(`user/document/${documentId}`, onSuccess, onError);
  }

  requestSignCode(
    documentId: number,
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPut(`user/requestSignCode/${documentId}`, null, onSuccess, onError);
  }

  validateSignCode(
    data: SignRequestValidationDTO,
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost("user/validateSignCode", data, onSuccess, onError);
  }

  uploadDocument(
    file: File,
    itemId: number,
    onSuccess: SuccessCallback<string>,
    onError: ErrorCallback
  ): void {
    readFileAsBase64(file)
      .then((documentFile) => {
        const payload: DocumentUploadRequest = {
          fileName: file.name,
          contentType: file.type,
          documentFile,
          itemId,
        };

        worktimeApi.genericJsonPost(
          "user/uploadDocumentBase64",
          payload,
          onSuccess,
          onError
        );
      })
      .catch((error) => {
        onError(error as AxiosError);
      });
  }

  uploadSignaturePicture(
    documentId: number,
    dataUrl: string,
    onSuccess: SuccessCallback<unknown>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost("user/uploadSignaturePicture", `${documentId}&${dataUrl}`, onSuccess, onError);
  }

  private fetchDocumentsInfo(
    options: RequestOptions = {},
  ): Promise<DocumentsInfoDTO> {
    const forceRefresh = options.force === true;
    const now = Date.now();

    if (
      !forceRefresh &&
      this.documentsInfoCache &&
      now - this.documentsInfoCachedAt < DOCUMENTS_INFO_CACHE_TTL_MS
    ) {
      return Promise.resolve(this.documentsInfoCache);
    }

    if (!forceRefresh && this.documentsInfoRequest) {
      return this.documentsInfoRequest;
    }

    this.documentsInfoRequest = new Promise((resolve, reject) => {
      worktimeApi.genericGet(
        "user/documents-info",
        (response) => {
          this.documentsInfoCache = response ?? null;
          this.documentsInfoCachedAt = Date.now();
          this.documentsInfoRequest = null;
          resolve(response);
        },
        (error) => {
          this.documentsInfoRequest = null;
          reject(error);
        },
      );
    });

    return this.documentsInfoRequest;
  }
}

export const documentsApi = new DocumentsAPI();
export default documentsApi;
